import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { explicarError, mensajeDeCampo } from "../domain/errores";
import { useDialogoSuperior } from "./capaSuperior";
import Icono from "./Icono";

// Avisos emergentes (toasts) en vez de alert() y de los textos de error en
// línea. Errores: role="alert", se quedan 9 s y se pausan con el puntero o el
// foco encima. Éxitos e info: 4,5 s en una región aria-live educada. La
// cuenta regresiva es una animación CSS de la barrita inferior (se pausa sola
// con :hover y :focus-within); con movimiento reducido no hay animación y el
// aviso queda hasta que se cierra — nunca se pierde un error por no leerlo a
// tiempo.

export type TipoAviso = "error" | "exito" | "info";
type Aviso = {
  id: number;
  tipo: TipoAviso;
  titulo: string;
  mensaje?: string;
  // Cambia cuando llega un aviso idéntico: reinicia la cuenta y lo sacude en
  // vez de apilar copias.
  vez: number;
};
type Nuevo = Omit<Aviso, "id" | "vez">;

const MAXIMO = 4;
const Contexto = createContext<{
  mostrar: (a: Nuevo) => number;
  cerrar: (id: number) => void;
} | null>(null);

export function Avisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const siguiente = useRef(1);
  const mostrar = useCallback((a: Nuevo) => {
    const id = siguiente.current++;
    setAvisos((lista) => {
      const igual = lista.find(
        (x) =>
          x.tipo === a.tipo && x.titulo === a.titulo && x.mensaje === a.mensaje,
      );
      const resto = lista.filter((x) => x !== igual);
      return [...resto, { ...a, id, vez: (igual?.vez ?? 0) + 1 }].slice(
        -MAXIMO,
      );
    });
    return id;
  }, []);
  const cerrar = useCallback(
    (id: number) => setAvisos((lista) => lista.filter((x) => x.id !== id)),
    [],
  );
  useValidacionDeFormularios(mostrar);
  const valor = useMemo(() => ({ mostrar, cerrar }), [mostrar, cerrar]);
  return (
    <Contexto.Provider value={valor}>
      {children}
      <Region avisos={avisos} onCerrar={cerrar} />
    </Contexto.Provider>
  );
}

function Region({
  avisos,
  onCerrar,
}: {
  avisos: Aviso[];
  onCerrar: (id: number) => void;
}) {
  const dialogo = useDialogoSuperior();
  if (!avisos.length) return null;
  // `key` fuerza a montar de nuevo al cambiar de contenedor, para volver a
  // subir la región al tope de la capa superior (encima del modal nuevo).
  return createPortal(
    <Lista
      key={dialogo ? "modal" : "body"}
      avisos={avisos}
      onCerrar={onCerrar}
    />,
    dialogo ?? document.body,
  );
}

function Lista({
  avisos,
  onCerrar,
}: {
  avisos: Aviso[];
  onCerrar: (id: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // popover="manual" coloca la región en la capa superior del navegador,
  // por encima de la barra fija y de cualquier <dialog>, sin depender de
  // z-index. Se vuelve a mostrar cuando llega un aviso nuevo para quedar
  // encima de lo último que se abrió.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el?.showPopover) return;
    try {
      if (el.matches(":popover-open")) el.hidePopover();
      el.showPopover();
    } catch {
      // Navegador sin popover: queda como `position: fixed` normal.
    }
  }, [avisos.length]);
  return (
    <div ref={ref} popover="manual" className="avisos" aria-label="Avisos">
      <ol aria-live="polite" aria-relevant="additions">
        {avisos.map((a) => (
          <Tarjeta key={a.id} aviso={a} onCerrar={() => onCerrar(a.id)} />
        ))}
      </ol>
    </div>
  );
}

const ICONO = { error: "alerta", exito: "check", info: "info" } as const;

function Tarjeta({ aviso, onCerrar }: { aviso: Aviso; onCerrar: () => void }) {
  return (
    <li
      className="aviso"
      data-tipo={aviso.tipo}
      data-repetido={aviso.vez > 1 || undefined}
      role={aviso.tipo === "error" ? "alert" : undefined}
    >
      <Icono nombre={ICONO[aviso.tipo]} />
      <div className="aviso-texto">
        <strong>{aviso.titulo}</strong>
        {aviso.mensaje ? <p>{aviso.mensaje}</p> : null}
      </div>
      <button
        type="button"
        className="icon-button ghost"
        aria-label="Cerrar aviso"
        onClick={onCerrar}
      >
        <Icono nombre="cerrar" />
      </button>
      <span
        key={aviso.vez}
        className="aviso-tiempo"
        aria-hidden="true"
        onAnimationEnd={onCerrar}
      />
    </li>
  );
}

export function useAvisos() {
  const ctx = useContext(Contexto);
  if (!ctx) throw new Error("Falta <Avisos> por encima de este componente.");
  return ctx;
}

// Muestra `error` como aviso emergente mientras exista. Se retira solo cuando
// el error se limpia (setError(null)) o el componente se desmonta, así un
// error de un modal no queda flotando después de cerrarlo.
export function ErrorAviso({
  error,
  titulo,
}: {
  error: unknown;
  titulo?: string;
}) {
  const { mostrar, cerrar } = useAvisos();
  useEffect(() => {
    if (!error) return;
    const aviso = explicarError(error);
    const id = mostrar({
      tipo: "error",
      titulo: titulo ?? aviso.titulo,
      mensaje: aviso.mensaje,
    });
    return () => cerrar(id);
  }, [error, titulo, mostrar, cerrar]);
  return null;
}

// El navegador dispara `invalid` en cada campo que no pasa la validación
// nativa al enviar un formulario. Se cancela la burbuja del sistema, se
// marcan todos los campos inválidos y se muestra un único aviso con el nombre
// del primero, que recibe el foco.
function useValidacionDeFormularios(mostrar: (a: Nuevo) => number) {
  useEffect(() => {
    let avisado = false;
    function onInvalid(e: Event) {
      const campo = e.target;
      if (
        !(
          campo instanceof HTMLInputElement ||
          campo instanceof HTMLSelectElement ||
          campo instanceof HTMLTextAreaElement
        )
      )
        return;
      e.preventDefault();
      campo.setAttribute("aria-invalid", "true");
      if (avisado) return;
      avisado = true;
      // Un envío dispara `invalid` en cada campo, cada uno con su propio
      // punto de microtareas: un setTimeout agrupa todo el envío.
      setTimeout(() => {
        avisado = false;
      });
      campo.focus();
      const aviso = mensajeDeCampo({
        etiqueta: etiquetaDe(campo),
        etiquetaHtml: campo.tagName.toLowerCase(),
        tipo: campo instanceof HTMLInputElement ? campo.type : "",
        min: campo instanceof HTMLInputElement ? campo.min : undefined,
        max: campo instanceof HTMLInputElement ? campo.max : undefined,
        ayuda: campo.title || undefined,
        validity: campo.validity,
        mensajeNativo: campo.validationMessage,
      });
      mostrar({ tipo: "error", ...aviso });
    }
    // Apenas el campo vuelve a ser válido deja de marcarse en rojo.
    function onInput(e: Event) {
      const campo = e.target;
      if (
        campo instanceof HTMLElement &&
        campo.hasAttribute("aria-invalid") &&
        "checkValidity" in campo &&
        (campo as HTMLInputElement).validity.valid
      )
        campo.removeAttribute("aria-invalid");
    }
    document.addEventListener("invalid", onInvalid, true);
    document.addEventListener("input", onInput, true);
    document.addEventListener("change", onInput, true);
    return () => {
      document.removeEventListener("invalid", onInvalid, true);
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("change", onInput, true);
    };
  }, [mostrar]);
}

function etiquetaDe(campo: HTMLElement) {
  const deLabel =
    campo.closest("label")?.querySelector(":scope > span")?.textContent ??
    (campo.id
      ? document.querySelector(`label[for="${CSS.escape(campo.id)}"]`)
          ?.textContent
      : null);
  return (
    deLabel ??
    campo.getAttribute("aria-label") ??
    campo.getAttribute("placeholder") ??
    ""
  ).trim();
}
